import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Image as ImageIcon, Loader2, Paperclip, X, Download } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const ACCEPT = ".pdf,.docx,.txt,.md,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv";
const MAX = 5;
const ICONS = { image: ImageIcon, tableau: FileSpreadsheet, document: FileText };

async function telecharger(url, nom) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = nom; a.click(); URL.revokeObjectURL(a.href);
  } catch { toast.error("Téléchargement impossible"); }
}

export function AttachButton({ contexte, sessionId, pieces, setPieces, disabled, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (pieces.length + files.length > MAX) { toast.error(`${MAX} fichiers maximum par message`); return; }
    setBusy(true);
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} : 15 Mo maximum`); continue; }
      const fd = new FormData();
      fd.append("file", f); fd.append("contexte", contexte);
      if (sessionId) fd.append("session_id", sessionId);
      try {
        const { data } = await api.post("/ia/pieces-jointes", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPieces((p) => [...p, data]);
      } catch (err) { toast.error(`${f.name} : ${formatApiError(err.response?.data?.detail) || "envoi impossible"}`); }
    }
    setBusy(false);
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid={`${testId}-file-input`} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={disabled || busy} data-testid={`${testId}-attach-btn`} title="Joindre un fichier"
            className="h-10 w-10 shrink-0 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-[#C5A059] hover:text-[#A88846] flex items-center justify-center transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuItem onSelect={() => ref.current?.click()} data-testid={`${testId}-attach-choose`}>
            <Paperclip className="h-4 w-4 mr-2" />Joindre un fichier
          </DropdownMenuItem>
          <div className="px-2 pb-1 text-[11px] text-slate-500 leading-snug">PDF, Word, texte · JPG, PNG, WEBP · Excel, CSV — 5 fichiers, 15 Mo chacun</div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => telecharger("/ia/modeles/import-salaries.xlsx", "modele-import-salaries-oscop-ge.xlsx")} data-testid={`${testId}-modele-xlsx`}>
            <Download className="h-4 w-4 mr-2" />Modèle d'import salariés (Excel)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => telecharger("/ia/modeles/import-salaries.csv", "modele-import-salaries-oscop-ge.csv")} data-testid={`${testId}-modele-csv`}>
            <Download className="h-4 w-4 mr-2" />Modèle d'import salariés (CSV)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function PiecesChips({ pieces, onRemove, dark, testId }) {
  if (!pieces?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-pieces`}>
      {pieces.map((p) => {
        const Icon = ICONS[p.kind] || FileText;
        return (
          <span key={p.id || p.filename} data-testid={`${testId}-piece-${p.id}`}
            className={`inline-flex max-w-[220px] items-center gap-1 rounded px-2 py-0.5 text-[11px] ${dark ? "bg-white/15 text-white" : "bg-slate-100 text-slate-700"}`}>
            <Icon className="h-3 w-3 shrink-0" /><span className="truncate">{p.filename}</span>
            {onRemove && <button type="button" onClick={() => onRemove(p)} className="ml-0.5 hover:text-red-600" data-testid={`${testId}-piece-remove-${p.id}`}><X className="h-3 w-3" /></button>}
          </span>
        );
      })}
    </div>
  );
}

export async function retirerPiece(p, setPieces) {
  setPieces((x) => x.filter((y) => y.id !== p.id));
  try { await api.delete(`/ia/pieces-jointes/${p.id}`); } catch { /* déjà retirée */ }
}
